import type { Popover } from '@radix-ui/themes';
import { Heading, Text } from '@radix-ui/themes';
import clsx from 'clsx';
import { HubertButton } from 'components/HubertUI/HubertButton';
import HubertPopover from 'components/HubertUI/HubertPopover';
import HubertTabs from 'components/HubertUI/HubertTabs';
import type { ComponentProps } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { TMatrixEvaluationConditional, TMatrixSliderEvaluationWeighted } from 'types/evaluation.types';
import { EMatrixEvaluationType, isMatrixConditionalEvaluation, isWeightedEvaluation } from 'types/evaluation.types';
import type { TFormSlider } from '@hubertai/screening-question-types';
import MatrixEvaluationConditional from './MatrixEvaluationConditional';
import MatrixEvaluationTriggerButton from './MatrixEvaluationTriggerButton';
import MatrixEvaluationWeighted from './MatrixEvaluationWeighted';

interface Props extends ComponentProps<typeof Popover.Root> {
  allowedTabs: (EMatrixEvaluationType.Weighted | EMatrixEvaluationType.Conditional)[];
  form: TFormSlider;
  evaluation: TMatrixEvaluationConditional[] | TMatrixSliderEvaluationWeighted;
  onEvaluationChange: (e: { weight: number; policy: 'basic' } | TMatrixEvaluationConditional[] | object) => void;
}

const MatrixEvaluation = ({
  allowedTabs = [EMatrixEvaluationType.Weighted, EMatrixEvaluationType.Conditional],
  form,
  evaluation,
  onEvaluationChange,
  ...props
}: Props) => {
  // console.log('SliderEvaluation', templateId, nodeId, evaluation)
  const { t } = useTranslation('Templates');

  const tabs = useMemo(
    () => [
      { id: EMatrixEvaluationType.Weighted, name: t('evaluationPopover.matrixTabs.weighted'), description: '' },
      { id: EMatrixEvaluationType.Conditional, name: t('evaluationPopover.matrixTabs.conditional'), description: '' },
      { id: '', name: t('evaluationPopover.matrixTabs.notSet'), description: '' },
    ],
    [t],
  );

  const [originalEvaluationType, setOriginalEvaluationType] = useState<EMatrixEvaluationType | ''>('');
  const [_, setOriginalEvaluation] = useState<TMatrixEvaluationConditional[] | TMatrixSliderEvaluationWeighted>(
    evaluation,
  );

  const [evaluationType, setEvaluationType] = useState<EMatrixEvaluationType | ''>('');
  const [evaluationConditional, setEvaluationConditional] = useState<TMatrixEvaluationConditional[]>([]);

  const [evaluationWeighted, setEvaluationWeighted] = useState<TMatrixSliderEvaluationWeighted>({
    weight: 0,
    policy: 'basic',
  });

  useEffect(() => {
    setOriginalEvaluation(evaluation);

    if (isWeightedEvaluation(evaluation)) {
      setOriginalEvaluationType(EMatrixEvaluationType.Weighted);

      setEvaluationType(EMatrixEvaluationType.Weighted);
      setEvaluationConditional([]);
      setEvaluationWeighted(evaluation);
    } else if (isMatrixConditionalEvaluation(evaluation)) {
      setOriginalEvaluationType(EMatrixEvaluationType.Conditional);

      setEvaluationType(EMatrixEvaluationType.Conditional);
      setEvaluationConditional(evaluation);
      setEvaluationWeighted({
        weight: 0,
        policy: 'basic',
      });
    } else {
      setEvaluationType('');
      setOriginalEvaluationType('');
    }
  }, [evaluation]);

  const customContainer = document.getElementById('main-content') as HTMLElement;

  return (
    <HubertPopover {...props} modal={true}>
      <HubertPopover.Trigger>
        <MatrixEvaluationTriggerButton evaluationType={originalEvaluationType} />
      </HubertPopover.Trigger>

      <HubertPopover.Content
        portalContent={true}
        portalElement={customContainer}
        align="start"
        className="z-10"
        side="left"
      >
        <HubertTabs
          defaultValue={evaluationType}
          value={evaluationType}
          onValueChange={(e: any) => {
            setEvaluationType(e);
          }}
          activationMode="manual"
        >
          <HubertTabs.List>
            {tabs.map((tab) => {
              // @ts-ignore
              if (tab.id === '' || allowedTabs.includes(tab.id)) {
                return (
                  <HubertTabs.Trigger key={`slider-evaluation-popover-tab-trigger-${tab.id}`} value={tab.id}>
                    {tab.name}
                  </HubertTabs.Trigger>
                );
              }

              return null;
            })}
          </HubertTabs.List>

          {tabs.map((tab) => {
            return (
              <HubertTabs.Content
                value={tab.id}
                key={`slider-evaluation-popover-tab-content-${tab.id}`}
                className={clsx('min-h-[200px]', 'min-w-[450px]')}
              >
                {(() => {
                  if (tab.id === EMatrixEvaluationType.Conditional) {
                    return (
                      <MatrixEvaluationConditional
                        // @ts-ignore
                        form={form}
                        evaluation={evaluationConditional}
                        onEvaluationChange={(updatedEvaluation: any) => {
                          console.log('onEvaluationChange', updatedEvaluation);
                          setEvaluationConditional(updatedEvaluation);
                        }}
                      />
                    );
                  } else if (tab.id === EMatrixEvaluationType.Weighted) {
                    return (
                      <MatrixEvaluationWeighted
                        evaluation={evaluationWeighted}
                        onEvaluationChange={(x: number[]) => {
                          console.log('onEvaluationChange', x);
                          setEvaluationWeighted({
                            ...evaluationWeighted,
                            weight: x[0],
                          });
                        }}
                      />
                    );
                  } else {
                    return (
                      <HubertTabs.Content
                        value={tab.id}
                        key={`evaluation-popover-tab-content-${tab.id}`}
                        className="min-h-[200px] min-w-[300px]"
                      >
                        <Heading size="3" weight={'medium'}>
                          {tab.name}
                        </Heading>
                        <Text as="p" size="1">
                          {t('evaluationPopover.noEvaluationSet')}
                        </Text>
                      </HubertTabs.Content>
                    );
                  }
                })()}
              </HubertTabs.Content>
            );
          })}

          <div className={clsx('flew-row flex justify-between gap-2', 'hubert-vertical-center')}>
            {/* TODO(i18n): Category 4 — sentence split across JSX children with inline <Link>; restructure before extracting */}
            <span className="text-sm">
              Read about Hubert evaluations{' '}
              <Link to={'/evaluation'} target="_blank" className="text-info-500">
                here
              </Link>
              .
            </span>

            <div className={clsx('flew-row flex justify-between gap-2', 'hubert-vertical-center')}>
              <HubertPopover.Close asChild>
                <HubertButton
                  size="sm"
                  variant={'secondary'}
                  // disabled={!isEdited}
                >
                  {t('evaluationPopover.cancelButton')}
                </HubertButton>
              </HubertPopover.Close>

              <HubertPopover.Close asChild>
                <HubertButton
                  size="sm"
                  // disabled={!isEdited}
                  onClick={() => {
                    if (!onEvaluationChange) {
                      console.log('!onEvaluationChange');
                      return;
                    }

                    if (evaluationType === EMatrixEvaluationType.Weighted) {
                      onEvaluationChange(evaluationWeighted);
                      return;
                    }

                    if (evaluationType === EMatrixEvaluationType.Conditional) {
                      onEvaluationChange(evaluationConditional);
                      return;
                    }

                    onEvaluationChange({});
                    return;
                  }}
                >
                  {t('evaluationPopover.saveButton')}
                </HubertButton>
              </HubertPopover.Close>
            </div>
          </div>
        </HubertTabs>
      </HubertPopover.Content>
    </HubertPopover>
  );
};

export default MatrixEvaluation;
